<script setup lang="ts" generic="T extends string, U extends object = Record<string, never>">
import { computed } from 'vue';
import { TabGroup, TabList, Tab } from '@headlessui/vue';

export type TabItem<T extends string = string, U extends object = Record<string, never>> = {
  title: string;
  value: T;
} & U;

const props = defineProps<{
  modelValue: T | null;
  tabs: TabItem<T, U>[];
  showError?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: T | null): void;
}>();

const selectedIndex = computed(() =>
  props.modelValue == null ? -1 : props.tabs.findIndex((tab) => tab.value === props.modelValue),
);

const handleTabChange = (index: number) => {
  if (props.modelValue == null || props.tabs[index].value !== props.modelValue) {
    emit('update:modelValue', props.tabs[index].value);
  }
};
</script>

<template>
  <TabGroup :selected-index="selectedIndex" manual @change="handleTabChange">
    <TabList class="flex space-x-1 rounded-xl bg-gray-400/20 p-1" :class="{ 'ring-1 ring-red-500': showError }">
      <Tab v-for="tab in tabs" :key="tab.title" v-slot="{ selected }" as="template">
        <slot name="tab" :tab="tab" :selected="selected">
          <button
            class="text-md w-full rounded-lg py-2.5 font-medium leading-5 ring-white/60 ring-offset-2 focus:outline-none"
            :class="[
              selected && modelValue !== null
                ? 'bg-bridgit-vibrantGreen text-bridgit-primaryBtnText drop-shadow-md'
                : 'text-bridgit-primaryBtnText focus:ring-2',
            ]"
          >
            {{ tab.title }}
          </button>
        </slot>
      </Tab>
    </TabList>
  </TabGroup>
</template>
